import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import RbacManager from "../utilities/rbac";
import {
    Select,
    FormControl,
    Button,
    MenuItem,
    InputLabel,
    Grid,
    Paper,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalendarAlt,
    faList,
    faSnowflake,
    faPercentage,
    faImage,
    faUsers,
    faShieldHalved,
    faUserGear,
} from "@fortawesome/free-solid-svg-icons";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PAGE_WIDTH = 1200;
const SECTION_WIDTH = 540;
const SECTION_HEIGHT = 250;
const CARD_WIDTH = 210;
const CARD_HEIGHT = 130;

const sectionTitleStyle = {
    color: "var(--logo-deep-green)",
    fontWeight: 600,
    fontSize: "20px",
    marginBottom: "20px",
    paddingBottom: "12px",
    borderBottom: "2px solid var(--logo-deep-green)",
};

const sectionPaperStyle = {
    width: SECTION_WIDTH,
    height: SECTION_HEIGHT,
    padding: "24px",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    background: "#fff",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
};

const cardStyle = {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    padding: "16px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid #e0e0e0",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    boxSizing: "border-box",
    "&:hover": { transform: "translateY(-2px)" },
};

const iconStyle = {
    fontSize: "30px",
    marginBottom: "10px",
    display: "block",
};

const buttonStyle = {
    px: 3,
    py: 1.5,
    fontSize: "14px",
    fontWeight: 600,
    borderRadius: "8px",
    textTransform: "none",
    "&:hover": { filter: "brightness(0.95)" },
};

function DataManagement() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [selectedEntity, setSelectedEntity] = useState("");
    const [cutOffLoading, setcutOffLoading] = useState(false);

    const rbacMgr = new RbacManager(
        user?.userType === "employee" && user?.roles[0] !== "admin"
            ? user?.designation
            : user?.roles[0],
        "DataManagement"
    );

    const isV = rbacMgr.isV.bind(rbacMgr);
    const navigateTo = (path) => () => navigate(path);

    const handleCuttOffSubmit = async (entity) => {
        setcutOffLoading(true);
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/auth/run/runBatchCutOffEntityWise?entity=${entity}`
            );

            if (data.status?.toLowerCase() === "ok" && data?.details?.failedOrders?.length == 0) {
                Swal.fire({
    title: t("Sync Completed"),
    html: `
        <div style="margin-bottom: 16px;">
            ${t(data.message) || t("Order synchronization is complete. Please check the Orders page to verify the status.")}
        </div>
        <div style="color: #d93025; margin-bottom: 16px;">
            ${t("If required, the cutoff process can be run again after 30 minutes.")}
        </div>
    `,
    icon: "success",
    confirmButtonText: t("OK"),
});
            } else {
                const failedMessage = Object.entries(
                    (data?.failedOrders || [])
                        .filter((r) => r.status !== "success")
                        .reduce((acc, result) => {
                            const error = result?.error || "Unknown error";
                            acc[error] = (acc[error] || 0) + 1;
                            return acc;
                        }, {})
                )
                    .map(([error, count]) => `• ${error}: ${count} order(s)`)
                    .join("\n");

                Swal.fire({
                    title: t("Sync Failed"),
                    text:
                        t(failedMessage) || t(data.message) ||
                        t("Some orders failed during the cut-off process."),
                    icon: "error",
                    confirmButtonText: t("OK"),
                });
            }
        } catch (error) {
            console.error("An error occurred while running Cut-off:", error);
            Swal.fire({
                title: t("Cut-off Failed"),
                text:
                    error.response?.data?.message ||
                    t("An error occurred while running Cut-off."),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setcutOffLoading(false);
            setSelectedEntity("");
        }
    };

    const renderCard = ({ key, icon, color, title, description, onClick, visible }) =>
        visible ? (
            <Grid item key={key} sx={{ width: CARD_WIDTH, flex: "0 0 auto" }}>
                <Paper sx={cardStyle} onClick={onClick}>
                    <FontAwesomeIcon icon={icon} style={{ ...iconStyle, color }} />
                    <h3
                        style={{
                            margin: "0 0 6px 0",
                            color: "#333",
                            fontSize: "15px",
                            fontWeight: 600,
                            lineHeight: 1.2,
                        }}
                    >
                        {t(title)}
                    </h3>
                    <p
                        style={{
                            color: "#666",
                            fontSize: "12px",
                            margin: 0,
                            lineHeight: 1.35,
                        }}
                    >
                        {description}
                    </p>
                </Paper>
            </Grid>
        ) : null;

    const renderSection = (title, cardsData) => {
        if (!cardsData.some((c) => c.visible)) return null;

        return (
            <Paper sx={sectionPaperStyle}>
                <h2 style={sectionTitleStyle}>{t(title)}</h2>
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Grid
                        container
                        spacing={2}
                        wrap="nowrap"
                        sx={{
                            width: "100%",
                            justifyContent: "center",
                            alignItems: "center",
                            margin: 0,
                        }}
                    >
                        {cardsData.map(renderCard)}
                    </Grid>
                </div>
            </Paper>
        );
    };

    const cards = {
        customer: [
            {
                key: "creditUnblock",
                icon: faUsers,
                color: "var(--logo-red)",
                title: "Credit Unblock Date Editor",
                description: "Edit credit unblock dates",
                onClick: navigateTo("/creditUnblockDateEditor"),
                visible: isV("CreditUnblockDateEditor"),
            },
            {
                key: "pricingPolicy",
                icon: faPercentage,
                color: "var(--logo-light-green)",
                title: "Pricing Policy",
                description: "Manage pricing policies",
                onClick: navigateTo("/pricingPolicyEditor"),
                visible: isV("PricingPolicyEditor"),
            },
        ],
        product: [
            {
                key: "priceLists",
                icon: faList,
                color: "var(--logo-orange)",
                title: "Price Lists",
                description: "View and manage price lists",
                onClick: navigateTo("/priceListEditor"),
                visible: isV("PriceListsEditor"),
            },
            {
                key: "promotionalImages",
                icon: faImage,
                color: "var(--logo-deep-green)",
                title: "Promotional Images",
                description: "Manage promotional images and banners",
                onClick: navigateTo("/promotionalImageEditor"),
                visible: isV("PromotionalImageManagement"),
            },
        ],
        orders: [
            {
                key: "coolingPeriod",
                icon: faSnowflake,
                color: "var(--logo-cyan)",
                title: "Cooling Period",
                description: "Configure cooling period settings",
                onClick: navigateTo("/coolingPeriodEditor"),
                visible: isV("CoolingPeriodEditor"),
            },
            {
                key: "deliverySchedule",
                icon: faCalendarAlt,
                color: "var(--logo-red)",
                title: "Delivery Schedule",
                description: "Manage delivery schedules",
                onClick: navigateTo("/deliveryScheduleEditor"),
                visible: isV("DeliveryScheduleEditor"),
            },
        ],
        admin: [
            {
                key: "accessControl",
                icon: faShieldHalved,
                color: "var(--logo-deep-green)",
                title: "Access Control",
                description: "Manage role permissions and access",
                onClick: navigateTo("/accessControl"),
                visible: isV("accessControl"),
            },
            {
                key: "employeeManagement",
                icon: faUserGear,
                color: "var(--logo-orange)",
                title: "Employee Management",
                description: "Manage employee records",
                onClick: navigateTo("/employeeManagement"),
                visible: isV("employeeManagement"),
            },
        ],
    };

    const sections = [
        { title: "Customer Related Data", data: cards.customer },
        { title: "Product Related Data", data: cards.product },
        { title: "Orders Related Data", data: cards.orders },
        { title: "Admin Controls", data: cards.admin },
    ];

    return (
        <Sidebar title={t("General")}>
            <div style={{ display: "flex", justifyContent: "center" }}>
                <div
                    style={{
                        width: "100%",
                        maxWidth: `${PAGE_WIDTH}px`,
                        background: "#fff",
                        borderRadius: "16px",
                    }}
                >
                    {isV("dataManagementSection") && (
                        <Grid container spacing={3}>
                            {sections.map((section) => (
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    key={section.title}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "stretch",
                                    }}
                                >
                                    {renderSection(section.title, section.data)}
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {isV("EntityWiseCutOffSection") && (
                        <div style={{ marginTop: "32px" }}>
                            <h2 style={sectionTitleStyle}>{t("Entity Wise Cut-off")}</h2>

                            <Paper
                                sx={{
                                    padding: "32px",
                                    background:
                                        "linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)",
                                    borderRadius: "12px",
                                    border: "1px solid #e0e0e0",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "24px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <div style={{ flex: "1", minWidth: "250px" }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px",
                                                marginBottom: "16px",
                                            }}
                                        >
                                            <h3
                                                style={{
                                                    margin: 0,
                                                    color: "#333",
                                                    fontSize: "16px",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {t("Entity Wise Cut-off")}
                                            </h3>
                                        </div>
                                        <p
                                            style={{
                                                margin: "0 0 16px 0",
                                                color: "#666",
                                                fontSize: "13px",
                                            }}
                                        >
                                            Run cut-off process for specific entities. Select an
                                            entity and click "Run Cut-off" to execute the operation.
                                        </p>
                                    </div>

                                    <FormControl
                                        sx={{
                                            minWidth: "200px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <InputLabel>{t("Select Entity")}</InputLabel>
                                        <Select
                                            value={selectedEntity}
                                            label="Select Entity"
                                            onChange={(e) => setSelectedEntity(e.target.value)}
                                        >
                                            <MenuItem value="SHC">SHC</MenuItem>
                                            <MenuItem value="GMTC">GMTC</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Button
                                        variant="contained"
                                        sx={{
                                            ...buttonStyle,
                                            backgroundColor: "var(--logo-deep-green)",
                                            minWidth: "160px",
                                        }}
                                        disabled={!selectedEntity || cutOffLoading}
                                        onClick={() => handleCuttOffSubmit(selectedEntity)}
                                    >
                                        {cutOffLoading ? (
                                            <span
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        animation: "spin 1s linear infinite",
                                                    }}
                                                >
                                                    ⟳
                                                </span>
                                                Running...
                                            </span>
                                        ) : (
                                            "Run Cut-off"
                                        )}
                                    </Button>
                                </div>
                            </Paper>

                            <style>
                                {`
                                    @keyframes spin {
                                        from { transform: rotate(0deg); }
                                        to { transform: rotate(360deg); }
                                    }
                                `}
                            </style>
                        </div>
                    )}
                </div>
            </div>
        </Sidebar>
    );
}

export default DataManagement;